/**
 * Author: Artyom Goncharov
 *   Team: Client-Core
 *   User: agoncharov
 *   Date: 13.11.2014
 *   Time: 17:05
 */

import { IFormat } from "../api/IFormat";
import { Localize } from "../../utils/Localize";
import { Time } from "../../utils/time/Time";
import { TimeType } from "../../utils/time/TimeType";

/**
 * <p>
 *     The <code>CascadeDurationFormat</code> class is for cases when you need dynamically switch time templates
 *     depending on the amount of seconds passed as parameter.
 * </p>
 */
export class CascadeDurationFormat implements IFormat {
    /**
     * @private
     */
    private _templates: string[];

    /**
     * <p>
     *     This formatter is for cases when you need dynamically switch templates depending on amount of seconds,
     *     see examples.
     * </p>
     *
     * <p>
     *     Format:
     *
     *     <ul>
     *         <li>replacement_field ::=  "{" field_name [":" format_spec] "}"</li>
     *         <li>field_name        ::=  "days" | "hours" | "minutes" | "seconds"</li>
     *         <li>format_spec       ::=  [[fill]align][sign][#][0][width][.precision][type]</li>
     *         <li>fill              ::=  &lt;a character other than '}'&gt;</li>
     *         <li>align             ::=  "&lt;" | "&gt;" | "=" | "^"</li>
     *         <li>sign              ::=  "+" | "-" | " "</li>
     *         <li>width             ::=  integer</li>
     *         <li>precision         ::=  integer</li>
     *         <li>type              ::=  "b" | "c" | "d" | "e" | "E" | "f" | "F" | "g" | "G" | "n" | "o" | "x" | "X" | "%"</li>
     *     </ul>
     * </p>
     *
     * <p>
     *     Note: Though all those options are valid currently implemented only these:
     *
     *     <ul>
     *         <li>fill</li>
     *         <li>align: "&gt;"</li>
     *         <li>width</li>
     *         <li>precision</li>
     *         <li>type: "d" | "f" | "F"</li>
     *     </ul>
     * </p>
     *
     * @example
     *
     * <listing version="3.0">
     *
     *     let formatter:CascadeDurationFormat;
     *
     *     formatter = new CascadeDurationFormat(
     *         "{hours:02}:{minutes:02}:{seconds:02}",
     *         null,
     *         null,
     *         null,
     *         "{days:.1} days"
     *     );
     *
     *     // produces "00:00:00"
     *     formatter.apply( 0 );
     *
     *     // produces "00:01:40"
     *     formatter.apply( 100 );
     *
     *     // produces "00:16:40"
     *     formatter.apply( 1000 );
     *
     *     // produces "2,3 days"
     *     formatter.apply( 200500);
     *
     * </listing>
     *
     * @param milliseconds template String.
     * @param seconds template String.
     * @param minutes template String.
     * @param hours template String.
     * @param days template String.
     * @param weeks template String.
     * @param months template String.
     * @param years template String.
     * @param millenniums template String.
     *
     * @return a custom cascade formatter.
     */
    constructor(
        milliseconds: string = null,
        seconds: string = null,
        minutes: string = null,
        hours: string = null,
        days: string = null,
        weeks: string = null,
        months: string = null,
        years: string = null,
        millenniums: string = null
    ) {
        this._templates = [milliseconds, seconds, minutes, hours, days, weeks, months, years, millenniums];
    }

    /**
     * Format the <code>seconds</code> value using the format that fits to the value provided.
     *
     * @param seconds Any amount of seconds to be formatted.
     * @param roundTo The biggest time quant you want to have, described in TimeType.
     *
     * @return String with the representation of the time converted.
     */
    public apply(seconds: number, roundTo: TimeType = null): string {
        if (!roundTo) {
            let index: number = this._templates.length;

            roundTo = TimeType.MILLISECONDS;

            while (index--) {
                if (this._templates[index]) {
                    roundTo = TimeType.values[index];
                    break;
                }
            }
        }

        let time: Time = new Time(seconds, TimeType.SECONDS, roundTo);
        let major: number = Math.min(TimeType.values.indexOf(time.major) + 1, this._templates.length);
        let format: string;

        while (major-- !== 0) {
            format = this._templates[major];

            if (format) {
                break;
            }
        }

        if (!format) {
            return "";
        }

        return Localize.timeCustom(format, seconds, roundTo);
    }
}
